import { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Loader2, Search, ArrowLeft, LifeBuoy } from "lucide-react";
import DOMPurify from "dompurify";

const CATEGORY_LABELS = {
  "getting-started": "Getting started",
  documentation: "Documenting your rental",
  sharing: "Sharing & landlords",
  disputes: "Disputes & letters",
  deadlines: "Deadlines & guides",
};

function renderMarkdown(md) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => DOMPurify.sanitize(esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"));
  return (md || "").split("\n").map((line, i) => {
    const t = line.trim();
    const k = `${i}-${t.slice(0, 24)}`;
    if (!t) return <div key={k} className="h-2" />;
    if (/^\d+\.\s/.test(t)) return <p key={k} className="text-[15px] text-[#334155] leading-relaxed ml-1" dangerouslySetInnerHTML={{ __html: inline(t) }} />;
    if (t.startsWith("- ")) return <li key={k} className="ml-5 list-disc text-[15px] text-[#334155] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(t.slice(2)) }} />;
    return <p key={k} className="text-[15px] text-[#334155] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(t) }} />;
  });
}

export default function HelpCenter() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const location = useLocation();

  useEffect(() => {
    document.title = "Help Center · TenantSeal";
    axios.get(`${API}/help/articles`)
      .then(({ data }) => setGroups(data.categories || []))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, []);

  // Scroll to a linked article once loaded.
  useEffect(() => {
    if (loading || !location.hash) return;
    const id = location.hash.slice(1);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  }, [loading, location.hash]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return groups;
    return groups
      .map((g) => ({
        ...g,
        articles: g.articles.filter(
          (a) => a.title.toLowerCase().includes(term) || a.body_markdown.toLowerCase().includes(term),
        ),
      }))
      .filter((g) => g.articles.length > 0);
  }, [groups, q]);

  return (
    <div className="min-h-screen bg-[#EEF2F6]">
      <header className="border-b border-[#E2E8F0] bg-white/90 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/" data-testid="help-back-home" className="flex items-center gap-1.5 text-sm text-[#475569] hover:text-brand">
            <ArrowLeft size={15} /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="help-center">
        <div className="flex items-center gap-2 text-brand"><LifeBuoy size={20} /><span className="text-sm font-semibold uppercase tracking-wider">Help Center</span></div>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-navy">How can we help?</h1>
        <p className="mt-2 text-[#475569]">Short answers to how TenantSeal works — from documenting your place to disputing deductions.</p>

        <div className="relative mt-6">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input
            data-testid="help-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search help articles…"
            className="h-12 rounded-xl pl-10 bg-white border-[#E2E8F0]"
          />
        </div>

        {loading ? (
          <div className="mt-10 grid place-items-center"><Loader2 className="animate-spin text-brand" /></div>
        ) : filtered.length === 0 ? (
          <p className="mt-10 text-center text-[#64748B]" data-testid="help-no-results">No articles match “{q}”. Try a different search.</p>
        ) : (
          <div className="mt-8 space-y-10">
            {filtered.map((g) => (
              <section key={g.category} data-testid={`help-category-${g.category}`}>
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8]">{CATEGORY_LABELS[g.category] || g.category}</h2>
                <div className="mt-3 space-y-4">
                  {g.articles.map((a) => (
                    <article
                      key={a.slug}
                      id={a.slug}
                      data-testid={`help-article-${a.slug}`}
                      className="scroll-mt-24 rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6"
                    >
                      <h3 className="font-heading text-xl font-semibold text-navy">{a.title}</h3>
                      <div className="mt-3 space-y-2">{renderMarkdown(a.body_markdown)}</div>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <footer className="mt-12 py-6 text-center border-t border-[#E2E8F0]">
          <p className="text-xs text-[#94A3B8]">TenantSeal is not a law firm and does not provide legal advice.</p>
          <Link to="/guides" className="mt-2 inline-block text-sm text-brand hover:underline">Browse state deposit guides →</Link>
        </footer>
      </main>
    </div>
  );
}
